import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TableSearchComponent } from '../../shared/table-search/table-search.component';
import { TableComponent } from '../../shared/table/table.component';
import { Subject, take, takeUntil } from 'rxjs';
import { RowData } from '../../shared/interfaces/table.data';
import { Application, ApplicationData, ApplicationTableData, AppVersion, Org } from '../../interfaces/application';
import { MatTableDataSource } from '@angular/material/table';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { AddApplicationModalComponent } from './add-application-modal/add-application-modal.component';
import {
  AddAppVersionModalComponent,
  AddAppVersionModalProps,
} from './add-app-version-modal/add-app-version-modal.component';
import {
  EditApplicationModalComponent,
  EditApplicationModalProps,
} from './edit-application-modal/edit-application-modal.component';
import { AppService } from '../../services/application/app.service';
import { AppManagementStateService } from '../../services/application/app-mangement-state.service';
import { OrgManagementStateService } from '../../services/organization/org-management-state.service';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { ModalDialogComponent } from '../../shared/modal-dialog/modal-dialog.component';

@Component({
  selector: 'app-application-management',
  standalone: true,
  imports: [TableSearchComponent, TableComponent, ButtonsComponent, DragDropModule],
  templateUrl: './application-management.component.html',
  styleUrl: './application-management.component.css',
  providers: [AppManagementStateService, OrgManagementStateService],
})
export class ApplicationManagementComponent implements OnInit, OnDestroy {
  dataSource = new MatTableDataSource<RowData<ApplicationTableData>>([]);
  btnName: string = $localize`Add application`;
  versionbtnName: string = $localize`Add version`;
  btnMatFlat: string = 'mat-flat-button';
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  displayedColumnLabels: string[] = [
    $localize`Application Name`,
    $localize`Description`,
    $localize`Available Version`,
    $localize`Organizations`,
    $localize`Activity`,
  ];
  displayedColumns: string[] = ['applicationName', 'description', 'applicationVersion', 'organizations', 'activity'];

  editVersionModalTitle = $localize`Edit Application`;
  addAppModalTitle = $localize`Add Application`;
  addAppVersionModalTitle = $localize`Add Application Version`;

  private appStateService = inject(AppManagementStateService);
  private orgStateService = inject(OrgManagementStateService);
  private allAppVersions: AppVersion[] = [];
  private allApplications: ApplicationData[] = [];
  private allOrgs: Org[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private applicationService: AppService,
    private messageModal: MessageModalService
  ) {}

  ngOnInit() {
    this.appStateService.appsWithVersions$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (appsWithVersions) => {
        this.allAppVersions = appsWithVersions;
        const rowData: RowData<ApplicationTableData>[] = appsWithVersions.map((appV) => {
          return {
            data: {
              appVersionId: appV.appVersionId,
              applicationName: appV.name,
              description: appV.description,
              applicationVersion: appV.version,
              organizations: {
                list: appV.orgs.map((org) => org.name),
                count: appV.orgs.length,
              },
              activity: {
                status: appV.isArchived ? $localize`Inactive` : $localize`Active`,
                lastUpdated: appV.isArchived ? (appV.archivedAt ?? '') : appV.releaseDate,
              },
            },
            selected: false,
          };
        });
        this.dataSource = new MatTableDataSource<RowData<ApplicationTableData>>(rowData);
        this.dataSource.filterPredicate = this.createFilterPredicate();
      },
      error: (err) => {
        console.error('Error encountered while fetching applications', err);
        this.messageModal.openModal($localize`Error`, $localize`Invalid application response format`);
      },
    });

    this.orgStateService.orgs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (orgs) => {
        this.allOrgs = orgs;
      },
      error: (err: any) => {
        console.error('Error encountered while fetching organizations', err);
        this.messageModal.openModal($localize`Error`, $localize`Error fetching organizations list`);
      },
    });

    this.appStateService.apps$.pipe(takeUntil(this.destroy$)).subscribe((apps) => {
      this.allApplications = apps;
    });
  }

  // --- Action handlers for edit and delete buttons ---
  onEditApplication(row: ApplicationTableData) {
    this.applicationService
      .getAppVersionDetails(row.appVersionId)
      .pipe(take(1))
      .subscribe((appVersion) => {
        const application = this.allApplications.find((app) => app.appId === appVersion.appId) ?? {
          appId: appVersion.appId,
          name: row.applicationName,
          description: row.description,
          isPlugin: false,
          versions: [],
        };

        const dialogRef = this.dialog.open<EditApplicationModalComponent, EditApplicationModalProps>(
          EditApplicationModalComponent,
          {
            data: {
              application,
              appVersion,
              allAppVersions: this.allAppVersions,
              allOrgs: this.allOrgs,
              title: this.editVersionModalTitle,
            },
          }
        );

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((result) => {
            if (result) {
              this.handlePopupClose(result);
            }
          });
      });
  }

  onAddApplication(event: any) {
    const dialogRef = this.dialog.open(AddApplicationModalComponent, {
      data: {
        title: this.addAppModalTitle,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.handlePopupClose(result);
        }
      });
  }

  onAddVersionApplication(event: any) {
    const dialogRef = this.dialog.open<AddAppVersionModalComponent, AddAppVersionModalProps>(
      AddAppVersionModalComponent,
      {
        data: {
          allApplications: this.allApplications,
          allAppVersions: this.allAppVersions,
          allOrgs: this.allOrgs,
          title: this.addAppVersionModalTitle,
        },
      }
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.handlePopupClose(result);
        }
      });
  }

  private handlePopupClose(result: any): void {
    if (result && result.success) {
      this.appStateService.refresh();

      const title = $localize`Success`;
      let message = '';

      switch (result.action) {
        case 'add':
          message = $localize`Application has been created successfully.`;
          break;
        case 'version':
          message = $localize`New version has been added successfully.`;
          break;
        case 'edit':
          message = $localize`Changes have been saved successfully.`;
          break;
        default:
          message = $localize`The operation was successful.`;
      }

      // success modal
      this.messageModal.openModal(title, message);
    }
  }

  openDeleteConfirmationModal(row: ApplicationTableData) {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      data: {
        title: $localize`Delete Application?`,
        description: $localize`Once deleted, organizations will no longer be able to access this application.`,
        primaryButtonLabel: $localize`Yes, Delete`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true, // Show primary button
        showSecondaryButton: true, // Secondary button is always shown
      },
    });
    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onDeleteApplication(row, modalInstance);
    });
  }

  onDeleteApplication(row: ApplicationTableData, modalInstance: ModalDialogComponent) {
    const { appVersionId } = row;

    this.applicationService
      .deleteApp(appVersionId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.appStateService.refresh();
          modalInstance.closeModal(true);
        },
        error: (err) => {
          console.error(`Error while archiving application ${appVersionId}:`, err);
          modalInstance.showError($localize`An error occurred while archiving the application.`);
        },
      });
  }

  // --- Filter logic (can be adapted as needed) ---
  onFilterChanged(filterValue: string): void {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  createFilterPredicate(): (item: RowData<ApplicationTableData>, filter: string) => boolean {
    return (item: RowData<ApplicationTableData>, filter: string) => {
      const dataStr = Object.values(item.data).join(' ') + item.data.organizations.list.join(' ');
      return dataStr.toLowerCase().includes(filter);
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
